import { formatarAteDuasCasas } from '@/lib/formato';

import type { ApontamentoDaApi, LeiturasDoTurno, TurnoDaApi } from './api';
import { medidorInicialSugerido } from './medidor';

/**
 * O QUE O APONTAMENTO DE TURNO JÁ SABE (pedido do dono, 31/10/2026): *"nosso
 * foco é facilitar e agilizar ao máximo a vida de quem vai usar o sistema. As
 * melhorias que fizemos no formulário de combustível devem ser levadas aos
 * outros formulários"*.
 *
 * O turno de uma máquina se parece com o anterior dela: o mesmo operador, o
 * mesmo nome de turno, e o horímetro do começo é o do fim do último. A tela
 * da mesa pedia tudo de novo, em branco. Agora ela propõe, e diz de onde veio:
 *
 *  - o TURNO é o cadastrado da empresa para aquele dia (o que está em curso,
 *    quando o dia é hoje); sem turno cadastrado no dia, o do último turno da
 *    máquina; sem nenhum, "1º turno", o padrão de antes;
 *  - o HORÍMETRO DO INÍCIO é a última leitura antes do fim deste turno (a
 *    mesma consulta e a mesma regra do celular, `medidorInicialSugerido`); sem
 *    a resposta do servidor, o final do último turno da máquina;
 *  - o OPERADOR é o habitual da máquina, o do último turno dela; sem turno
 *    anterior, quem está lançando, se é apto a operar a máquina;
 *  - o HORÁRIO não é escrito no campo: sem ele, o servidor usa o do turno
 *    cadastrado com o mesmo nome. A tela só o mostra, para quem lança conferir.
 *
 * Sem tela aqui, para o teste alcançar a regra (este projeto não renderiza
 * tela em teste).
 */

/** Quantos dias para trás a tela procura o último turno da máquina. */
export const DIAS_DO_ULTIMO_TURNO = 60;

/** O turno mais recente da máquina: o dia mais novo e, no mesmo dia, o último lançado. */
export function ultimoTurnoDaMaquina(apontamentos: readonly ApontamentoDaApi[], equipamentoId: string): ApontamentoDaApi | null {
  let ultimo: ApontamentoDaApi | null = null;
  for (const a of apontamentos) {
    if (a.equipamentoId !== equipamentoId) continue;
    if (ultimo === null || a.dia > ultimo.dia || (a.dia === ultimo.dia && a.criadoEm > ultimo.criadoEm)) ultimo = a;
  }
  return ultimo;
}

function minutos(hora: string): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(hora);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Os turnos da empresa no dia da semana de `dia` (aaaa-mm-dd), na ordem do relógio. */
function turnosDoDia(turnos: readonly TurnoDaApi[], dia: string): readonly TurnoDaApi[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) return [];
  const diaSemana = new Date(`${dia}T12:00:00`).getDay();
  return turnos.filter((t) => t.diaSemana === diaSemana).toSorted((a, b) => a.inicio.localeCompare(b.inicio));
}

export type OrigemDoTurno = 'cadastro' | 'ultimo' | 'padrao';

/**
 * O nome do turno a propor. No dia de hoje, o turno cadastrado em curso agora
 * (ou o último que já começou); noutro dia, o primeiro do dia.
 */
export function turnoSugerido(entrada: {
  readonly turnos: readonly TurnoDaApi[];
  readonly dia: string;
  readonly hoje: string;
  readonly agora: Date;
  readonly ultimo: ApontamentoDaApi | null;
}): { readonly nome: string; readonly origem: OrigemDoTurno } {
  const doDia = turnosDoDia(entrada.turnos, entrada.dia);
  if (doDia.length > 0) {
    if (entrada.dia !== entrada.hoje) return { nome: doDia[0]?.nome ?? '', origem: 'cadastro' };
    const minuto = entrada.agora.getHours() * 60 + entrada.agora.getMinutes();
    const emCurso = doDia.find((t) => {
      const ini = minutos(t.inicio) ?? 0;
      const fim = minutos(t.fim) ?? 0;
      return fim > ini ? minuto >= ini && minuto < fim : minuto >= ini || minuto < fim;
    });
    const comecados = doDia.filter((t) => (minutos(t.inicio) ?? 0) <= minuto);
    return { nome: (emCurso ?? comecados.at(-1) ?? doDia[0])?.nome ?? '', origem: 'cadastro' };
  }
  if (entrada.ultimo && entrada.ultimo.turno.trim() !== '') return { nome: entrada.ultimo.turno, origem: 'ultimo' };
  return { nome: '1º turno', origem: 'padrao' };
}

/** O horário do turno cadastrado com este nome naquele dia, ou nulo. É o que o servidor usa sem a hora digitada. */
export function horarioDoTurnoCadastrado(
  turnos: readonly TurnoDaApi[],
  dia: string,
  nome: string,
): { readonly inicio: string; readonly fim: string } | null {
  const alvo = nome.trim().toLocaleUpperCase('pt-BR');
  if (alvo === '') return null;
  const achado = turnosDoDia(turnos, dia).find((t) => t.nome.trim().toLocaleUpperCase('pt-BR') === alvo);
  return achado ? { inicio: achado.inicio, fim: achado.fim } : null;
}

export type OrigemDoInicial = 'leitura' | 'ultimo-turno';

/**
 * O horímetro (ou odômetro) do início, em texto de campo ("4.115" vira "4115",
 * vírgula decimal). `esperando` enquanto a consulta das leituras deste turno
 * ainda vem: nada é proposto até ela chegar.
 */
export function inicialSugerido(
  leituras: LeiturasDoTurno | undefined,
  ultimo: ApontamentoDaApi | null,
  esperando: boolean,
): { readonly texto: string; readonly origem: OrigemDoInicial } | null {
  const final = ultimo === null ? null : Number(ultimo.horimetroFinal);
  const ultimaConhecida = final !== null && Number.isFinite(final) ? { valor: final } : null;
  const valor = medidorInicialSugerido(leituras, ultimaConhecida, esperando);
  if (valor === null) return null;
  return { texto: textoDoCampo(valor), origem: leituras === undefined ? 'ultimo-turno' : 'leitura' };
}

/** O número como o campo o guarda: sem ponto de milhar, vírgula decimal, até duas casas. */
export function textoDoCampo(valor: number): string {
  return formatarAteDuasCasas(valor).replaceAll('.', '');
}

export type OrigemDoOperador = 'ultimo-turno' | 'voce';

/**
 * O operador a propor: o do último turno da máquina, se continua na lista; sem
 * ele, quem está lançando, quando o servidor diz que é apto a operar a máquina.
 * O almoxarife ou o encarregado não viram operador por padrão.
 */
export function operadorSugerido(entrada: {
  readonly ultimo: ApontamentoDaApi | null;
  readonly eu: string | null;
  readonly euApto: boolean;
  readonly operadores: readonly { readonly id: string }[];
}): { readonly id: string; readonly origem: OrigemDoOperador } | null {
  const naLista = (id: string | null | undefined) => typeof id === 'string' && id !== '' && entrada.operadores.some((o) => o.id === id);
  if (naLista(entrada.ultimo?.operadorId)) return { id: entrada.ultimo?.operadorId ?? '', origem: 'ultimo-turno' };
  if (entrada.euApto && naLista(entrada.eu)) return { id: entrada.eu ?? '', origem: 'voce' };
  return null;
}

/**
 * Os campos que o sistema preenche, e a regra de quando preencher: só o que a
 * pessoa ainda não tocou. O que ela escreveu (ou apagou de propósito) é dela,
 * e a sugestão nova não passa por cima.
 */
export type CampoSugerido = 'turno' | 'inicial' | 'operadorId';

export function preencherSugestoes<V extends Readonly<Record<CampoSugerido, string>>>(
  valores: V,
  tocados: ReadonlySet<CampoSugerido>,
  sugestoes: Readonly<Partial<Record<CampoSugerido, string | null>>>,
): { readonly valores: V; readonly marcados: ReadonlySet<CampoSugerido> } {
  let proximo = valores;
  const marcados = new Set<CampoSugerido>();
  for (const campo of Object.keys(sugestoes) as CampoSugerido[]) {
    if (tocados.has(campo)) continue;
    const sugestao = sugestoes[campo] ?? '';
    if (proximo[campo] !== sugestao) proximo = { ...proximo, [campo]: sugestao };
    if (sugestao !== '') marcados.add(campo);
  }
  return { valores: proximo, marcados };
}
